import { useEffect, useState } from "react";
import { Bell, Heart, ShieldCheck } from "@phosphor-icons/react";
import useReveal from "../lib/useReveal";

/**
 * Three animated mini-demos that show — in motion — what PriceGuard actually does:
 *   1. Price-protection: tracked purchase price ticks DOWN below purchase price,
 *      then an alert pings in saying "money to recover".
 *   2. Wishlist saved: live price ticks down past where you first saved it,
 *      and the alert says "$X off since you saved it" (no target-price jargon
 *      yet — that concept is introduced later on the page).
 *   3. Warranty countdown: days-remaining counter visibly drops, then a polite
 *      reminder card slides in.
 *
 * Triggered by IntersectionObserver — runs once when scrolled into view.
 * Subtle, looped at a calm cadence, respects prefers-reduced-motion.
 */
export default function LivePreviewTrio() {
  const ref = useReveal();
  const [start, setStart] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setStart(true); return; }
    const obs = new MutationObserver(() => {
      if (el.classList.contains("pg-reveal-in")) {
        setTimeout(() => setStart(true), 250);
        obs.disconnect();
      }
    });
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    if (el.classList.contains("pg-reveal-in")) setStart(true);
    return () => obs.disconnect();
  }, [ref]);

  return (
    <section id="how" className="border-b border-[#e4e4e7] bg-white" data-testid="live-preview-trio">
      <div ref={ref} className="pg-reveal max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="pg-overline text-[#002FA7]">How PriceGuard works</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter mt-4 leading-[1.05]">
            Three jobs, done <span className="text-[#002FA7]">quietly.</span>
          </h2>
          <p className="text-base lg:text-lg text-[#52525b] leading-relaxed mt-5">
            Wishlist what you're planning to buy. Track what you've already bought for a price-adjustment claim if it drops. Vault every receipt and stay ahead of warranty expiries. Three quiet jobs, one app, no daily checking required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-10">
          <WishlistSavedDemo start={start} />
          <PriceProtectionDemo start={start} />
          <WarrantyCountdownDemo start={start} />
        </div>

        <p className="text-xs font-mono-pg text-[#52525b] uppercase tracking-widest mt-8">
          Illustrative · prices, dates and amounts are examples · the call is always the retailer's
        </p>
        <p className="text-xs text-[#52525b] mt-4 max-w-2xl leading-relaxed">
          Price-adjustment claims depend on each retailer's published policy. We currently support three NZ retailers that offer one - PB Tech, JB Hi-Fi and Harvey Norman - with more on the way. Wishlist tracking, the receipt vault and warranty alerts work for purchases from any retailer.
        </p>
      </div>
    </section>
  );
}

/* ---------- Shared LIVE badge with a pulsing dot ---------- */
function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-pg tracking-widest uppercase bg-[#002FA7] text-white px-1.5 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-white pg-pulse" />
      Live
    </span>
  );
}

/* ---------- 1) Price-protection demo ---------- */
function PriceProtectionDemo({ start }) {
  const [price, setPrice] = useState(3499);
  const [alertIn, setAlertIn] = useState(false);

  useEffect(() => {
    if (!start) return;
    const steps = [3499, 3399, 3299, 3199, 3099];
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      if (i < steps.length) {
        setPrice(steps[i]);
      } else {
        clearInterval(interval);
        setTimeout(() => setAlertIn(true), 350);
      }
    }, 480);
    return () => clearInterval(interval);
  }, [start]);

  const drop = 3499 - price;
  return (
    <div className="bg-white p-6 lg:p-8 relative overflow-hidden" data-testid="demo-price-protection">
      <div className="flex items-center justify-between mb-3">
        <div className="pg-overline">Step 02 · After you buy</div>
        <LiveBadge />
      </div>
      <p className="text-sm text-[#0a0a0a] leading-relaxed mb-5">
        <strong>Get notified when a recent purchase drops in price</strong>, and let PriceGuard help you claim back the difference. We draft a polite, retailer-ready email - you just read it, send it.
      </p>
      <div className="font-heading text-base font-bold tracking-tight">LG OLED C3 65"</div>
      <div className="text-[11px] font-mono-pg text-[#52525b] mt-1 uppercase tracking-widest">Tracked purchase · price adjustment window open</div>
      <div className="mt-6">
        <div className="text-[11px] font-mono-pg text-[#52525b] uppercase tracking-widest">You paid</div>
        <div className="font-heading text-2xl font-extrabold tracking-tight line-through text-[#52525b] mt-0.5">$3,499</div>
      </div>
      <div className="mt-4">
        <div className="text-[11px] font-mono-pg text-[#52525b] uppercase tracking-widest">Today's price</div>
        <div className={`font-heading text-4xl font-black tracking-tighter mt-0.5 transition-colors duration-500 ${drop > 0 ? "text-[#16A34A]" : "text-black"}`} data-testid="demo-price-protection-current">
          ${price.toLocaleString("en-NZ")}
        </div>
      </div>
      <div
        className={`mt-6 border border-[#16A34A]/50 bg-[#16A34A]/[0.06] px-4 py-3 transition-all duration-500 ${alertIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
        data-testid="demo-price-protection-alert"
        aria-hidden={!alertIn}
      >
        <div className="flex items-center gap-2">
          <Bell size={14} weight="fill" color="#16A34A" />
          <span className="pg-overline text-[#16A34A] text-[10px]">Drop detected · email drafted</span>
        </div>
        <div className="text-sm font-bold mt-1">You can ask for ${drop.toLocaleString("en-NZ")} back</div>
      </div>
    </div>
  );
}

/* ---------- 2) Wishlist saved demo — TV with a juicy $1,000 drop ---------- */
function WishlistSavedDemo({ start }) {
  const startPrice = 2999;
  const minPrice = 1999;
  const [price, setPrice] = useState(startPrice);
  const [hitIn, setHitIn] = useState(false);

  useEffect(() => {
    if (!start) return;
    const path = [2999, 2799, 2599, 2399, 2199, 2099, 1999];
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      if (i < path.length) {
        setPrice(path[i]);
      } else {
        clearInterval(interval);
        setTimeout(() => setHitIn(true), 250);
      }
    }, 360);
    return () => clearInterval(interval);
  }, [start]);

  const off = startPrice - price;
  const total = startPrice - minPrice;
  const pct = Math.round((off / total) * 100);
  const pctOff = Math.round((off / startPrice) * 100);

  return (
    <div className="bg-white p-6 lg:p-8 relative overflow-hidden" data-testid="demo-wishlist-target">
      <div className="flex items-center justify-between mb-3">
        <div className="pg-overline">Step 01 · Before you buy</div>
        <LiveBadge />
      </div>
      <p className="text-sm text-[#0a0a0a] leading-relaxed mb-5">
        <strong>Add anything you'd love to own.</strong> We watch every day and tell you the moment it goes on a serious sale. No daily checking, no FOMO, no paying full price out of habit.
      </p>
      <div className="font-heading text-base font-bold tracking-tight">Samsung 75" Neo QLED</div>
      <div className="text-[11px] font-mono-pg text-[#52525b] mt-1 uppercase tracking-widest">Wishlist · saved at ${startPrice.toLocaleString("en-NZ")}</div>

      <div className="mt-6">
        <div className="text-[11px] font-mono-pg text-[#52525b] uppercase tracking-widest">Live price</div>
        <div className={`font-heading text-4xl font-black tracking-tighter mt-0.5 transition-colors duration-500 ${off > 0 ? "text-[#16A34A]" : "text-black"}`} data-testid="demo-wishlist-current">
          ${price.toLocaleString("en-NZ")}
        </div>
        <div className="text-[11px] font-mono-pg uppercase tracking-widest mt-3 h-[14px]">
          {off > 0 ? <span className="text-[#16A34A] font-bold">↓ ${off.toLocaleString("en-NZ")} off · {pctOff}% cheaper</span> : <span className="text-[#52525b]">Waiting for a drop</span>}
        </div>
      </div>

      <div className="mt-5 relative h-2 bg-[#f0f0f2] rounded-sm">
        <div
          className="absolute inset-y-0 left-0 bg-[#16A34A] rounded-sm transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div
        className={`mt-5 border border-[#16A34A]/50 bg-[#16A34A]/[0.06] px-4 py-3 transition-all duration-500 ${hitIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
        data-testid="demo-wishlist-alert"
        aria-hidden={!hitIn}
      >
        <div className="flex items-center gap-2">
          <Heart size={14} weight="fill" color="#16A34A" />
          <span className="pg-overline text-[#16A34A] text-[10px]">Big drop · time to act</span>
        </div>
        <div className="text-sm font-bold mt-1">${off.toLocaleString("en-NZ")} cheaper than when you saved it.</div>
      </div>
    </div>
  );
}

/* ---------- 3) Warranty countdown demo ---------- */
function WarrantyCountdownDemo({ start }) {
  const [days, setDays] = useState(60);
  const [remindIn, setRemindIn] = useState(false);

  useEffect(() => {
    if (!start) return;
    const path = [60, 52, 44, 38, 33, 30];
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      if (i < path.length) {
        setDays(path[i]);
      } else {
        clearInterval(interval);
        setTimeout(() => setRemindIn(true), 250);
      }
    }, 420);
    return () => clearInterval(interval);
  }, [start]);

  return (
    <div className="bg-white p-6 lg:p-8 relative overflow-hidden" data-testid="demo-warranty">
      <div className="flex items-center justify-between mb-3">
        <div className="pg-overline">Step 03 · While you own it</div>
        <LiveBadge />
      </div>
      <p className="text-sm text-[#0a0a0a] leading-relaxed mb-5">
        <strong>We keep your receipts safe</strong>, remind you before warranty cover expires, and make it easy to access and share your records with your insurer or accountant.
      </p>
      <div className="font-heading text-base font-bold tracking-tight">Dyson V15 Detect</div>
      <div className="text-[11px] font-mono-pg text-[#52525b] mt-1 uppercase tracking-widest">Bought 22 months ago · 2-yr warranty</div>

      <div className="mt-6">
        <div className="text-[11px] font-mono-pg text-[#52525b] uppercase tracking-widest">Cover ends in</div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <div className={`font-heading text-5xl font-black tracking-tighter transition-colors duration-500 ${days <= 30 ? "text-[#D97706]" : "text-black"}`} data-testid="demo-warranty-days">
            {days}
          </div>
          <div className="font-mono-pg text-sm font-bold text-[#52525b]">days</div>
        </div>
      </div>

      <div className="mt-5 h-2 bg-[#f0f0f2] rounded-sm">
        <div
          className="h-full bg-[#D97706] rounded-sm transition-all duration-500 ease-out"
          style={{ width: `${Math.min(100, (1 - days / 60) * 100)}%` }}
        />
      </div>

      <div
        className={`mt-5 border border-[#D97706]/50 bg-[#D97706]/[0.06] px-4 py-3 transition-all duration-500 ${remindIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
        data-testid="demo-warranty-alert"
        aria-hidden={!remindIn}
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} weight="fill" color="#D97706" />
          <span className="pg-overline text-[#D97706] text-[10px]">Reminder ready</span>
        </div>
        <div className="text-sm font-bold mt-1">Got a fault? We'll draft your CGA-anchored claim email.</div>
      </div>
    </div>
  );
}
